import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { LOOKUP_QUERY_KEYS, LookupQueries } from './query';
import type {
  LookupTablesData,
  DivisionAndDistrictOrganization,
  DivisionAndDistrictOption,
  UseHelpCategoriesQueryOptions,
} from './types';
import { useAuth, useUserProfile } from '@hooks';

const emptyLookupTablesData: LookupTablesData = {
  ARTypes: [],
  Cowardins: [],
  HGMCodes: [],
  StateRegulations: [],
  DivisionAndDistricts: [],
  FormVersions: [],
};

const useLookupAccess = () => {
  const { isAuthenticated } = useAuth();
  const { userHasProfile } = useUserProfile();

  return isAuthenticated && userHasProfile;
};

const mapOrganizationToOption = (
  organization: DivisionAndDistrictOrganization,
  type: DivisionAndDistrictOption['type']
): DivisionAndDistrictOption => ({
  value: organization.orgID,
  label: organization.orgName,
  type,
  code: organization.shortName,
  parentId: organization.parentOrgID,
  orgID: organization.orgID,
  orgName: organization.orgName,
  parentOrgID: organization.parentOrgID,
  shortName: organization.shortName,
});

export const useLookupTablesQuery = () => {
  const canLoad = useLookupAccess();

  return useQuery({
    queryKey: [LOOKUP_QUERY_KEYS.GET_ALL_TABLES],
    queryFn: LookupQueries.getAllTables,
    enabled: canLoad,
    staleTime: 1000 * 60 * 60,
  });
};

export const useLookupTableFormVersions = () => {
  const { data: lookupTableData = emptyLookupTablesData } = useLookupTablesQuery();
  return lookupTableData.FormVersions;
};

export const useLookupTableDivisionsAndDistricts = () => {
  const { data: lookupTableData = emptyLookupTablesData } = useLookupTablesQuery();
  return lookupTableData.DivisionAndDistricts;
};

export const useDivisionsAndDistrictsQuery = (enabled = true) =>
  useQuery({
    queryKey: [LOOKUP_QUERY_KEYS.GET_DIVISIONS_AND_DISTRICTS],
    queryFn: LookupQueries.getDivisionsAndDistricts,
    enabled,
    staleTime: 1000 * 60 * 60,
  });

export const useDivisionsAndDistrictsOrganizations = (enabled = true) => {
  const query = useDivisionsAndDistrictsQuery(enabled);
  const data = query.data;
  const headquarters = useMemo(
    () => (data?.headquarters ? mapOrganizationToOption(data.headquarters, 'HQ') : null),
    [data?.headquarters]
  );
  const divisions = useMemo(
    () => data?.divisions.map((division) => mapOrganizationToOption(division, 'division')) ?? [],
    [data?.divisions]
  );
  const districts = useMemo(
    () => data?.districts.map((district) => mapOrganizationToOption(district, 'district')) ?? [],
    [data?.districts]
  );
  const usaceOrganizations = useMemo(
    () => [...(headquarters ? [headquarters] : []), ...divisions, ...districts],
    [headquarters, divisions, districts]
  );
  return {
    ...query,
    data,
    organizationOptions: usaceOrganizations,
    headquartersOption: headquarters,
    divisionOptions: divisions,
    districtOptions: districts,
  };
};

export const useDistrictsQuery = (enabled = true) =>
  useQuery({
    queryKey: [LOOKUP_QUERY_KEYS.GET_DISTRICTS, 'list'],
    queryFn: LookupQueries.getDistrictsList,
    enabled,
    staleTime: 1000 * 60 * 60,
  });

export const useDistrictsByCodeQuery = (enabled = true) =>
  useQuery({
    queryKey: [LOOKUP_QUERY_KEYS.GET_DISTRICTS],
    queryFn: LookupQueries.getDistricts,
    enabled,
    staleTime: 1000 * 60 * 60,
  });

export const useNwpGpPermitTypesQuery = (enabled = true) =>
  useQuery({
    queryKey: [LOOKUP_QUERY_KEYS.GET_NWP_GP_PERMIT_TYPES],
    queryFn: LookupQueries.getNwpGpPermitTypes,
    enabled,
    staleTime: 1000 * 60 * 60,
  });

export const useCreditUnitsByDistrictQuery = (district: string | null | undefined, enabled = true) =>
  useQuery({
    queryKey: [LOOKUP_QUERY_KEYS.GET_CREDIT_UNITS_BY_DISTRICT, district ?? 'none'],
    queryFn: () => LookupQueries.getCreditUnitsForDistrict(district as string),
    enabled: enabled && !!district,
    staleTime: 1000 * 60 * 30,
  });

export const useUpdateStateRegulations = () => {
  const queryClient = useQueryClient();

  return (stateRegs: LookupTablesData['StateRegulations']) => {
    queryClient.setQueryData<LookupTablesData>([LOOKUP_QUERY_KEYS.GET_ALL_TABLES], (prev) =>
      prev ? { ...prev, StateRegulations: stateRegs } : prev
    );
  };
};

export const useHelpCategoriesQuery = ({ active = true, enabled = true }: UseHelpCategoriesQueryOptions = {}) =>
  useQuery({
    queryKey: [LOOKUP_QUERY_KEYS.GET_HELP_CATEGORIES, active],
    queryFn: () => LookupQueries.getHelpCategories(active),
    enabled,
  });
